import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Layout, BookOpen, FileText, Trash2, Edit3, ExternalLink, LogOut, Search } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import SitemapEngine from '../../components/admin/SitemapEngine';

interface Post {
  id: string;
  title: string;
  slug: string;
  type: 'blog' | 'case_study';
  created_at: string;
  published: boolean;
  category: string;
}

export default function Dashboard() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'blog' | 'case_study'>('all');
  const navigate = useNavigate();

  useEffect(() => {
    // Basic auth check
    const session = localStorage.getItem('gt_admin_session');
    if (!session) {
      navigate('/admin/login');
      return;
    }

    fetchPosts();
  }, [navigate]);

  async function fetchPosts() {
    setLoading(true);
    const { data, error } = await supabase
      .from('cms_posts')
      .select('id, title, slug, type, created_at, published, category')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching posts:', error);
    } else {
      setPosts(data || []);
    }
    setLoading(false);
  }

  async function deletePost(id: string) {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    const { error } = await supabase
      .from('cms_posts')
      .delete()
      .eq('id', id);

    if (error) {
      alert('Error deleting post: ' + error.message);
    } else {
      fetchPosts();
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('gt_admin_session');
    navigate('/admin/login');
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || post.type === filterType;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <main className="pt-32 pb-24 home-section-bg">
        <div className="absolute inset-0 bg-grid-pattern opacity-60 pointer-events-none"></div>

        <div className="container mx-auto px-6 relative z-10">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">CMS Dashboard</h1>
              <p className="text-slate-400">Manage your blogs and case studies</p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-6 py-3 bg-white/5 border border-white/10 text-white rounded-xl font-bold hover:bg-white/10 transition-all"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
              <Link
                to="/admin/editor"
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl font-bold hover:shadow-2xl hover:shadow-teal-500/50 transition-all"
              >
                <Plus className="w-5 h-5" />
                Create New Content
              </Link>
            </div>
          </div>

          {/* Stats & Search */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="glass p-6 rounded-2xl border border-white/10 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center">
                <Layout className="w-6 h-6 text-teal-400" />
              </div>
              <div>
                <div className="text-xs text-slate-400 uppercase tracking-widest font-bold">Total Content</div>
                <div className="text-2xl font-bold text-white">{posts.length}</div>
              </div>
            </div>
            <div className="glass p-6 rounded-2xl border border-white/10 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <div className="text-xs text-slate-400 uppercase tracking-widest font-bold">Blogs</div>
                <div className="text-2xl font-bold text-white">{posts.filter(p => p.type === 'blog').length}</div>
              </div>
            </div>
            <div className="glass p-6 rounded-2xl border border-white/10 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <div className="text-xs text-slate-400 uppercase tracking-widest font-bold">Case Studies</div>
                <div className="text-2xl font-bold text-white">{posts.filter(p => p.type === 'case_study').length}</div>
              </div>
            </div>
            
            {/* Search and Filter */}
            <div className="glass p-6 rounded-2xl border border-white/10 flex flex-col justify-center">
               <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full bg-slate-900 border border-white/10 rounded-lg pl-9 py-2 text-sm text-white focus:outline-none focus:border-teal-500"
                    />
                  </div>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value as any)}
                    className="bg-slate-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-teal-500"
                  >
                    <option value="all">All</option>
                    <option value="blog">Blogs</option>
                    <option value="case_study">Case Studies</option>
                  </select>
               </div>
            </div>
          </div>

          {/* Sitemap Engine - Manual Indexing Control */}
          <div className="mb-12">
             <SitemapEngine />
          </div>

          {/* Posts List */}
          <div className="glass rounded-3xl border border-white/10 overflow-hidden shadow-2xl">
            {loading ? (
              <div className="py-20 text-center text-slate-400">Loading your content excellence...</div>
            ) : filteredPosts.length === 0 ? (
              <div className="py-20 text-center">
                <div className="text-slate-400 mb-4">No content found matching your filters.</div>
                <Link to="/admin/editor" className="text-teal-400 hover:underline">Start writing your first masterpiece</Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-white/5 border-b border-white/10">
                      <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Title</th>
                      <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-wider text-center">Type</th>
                      <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Category</th>
                      <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Created At</th>
                      <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-wider text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filteredPosts.map((post) => (
                      <tr key={post.id} className="hover:bg-white/[0.02] transition-colors group">
                        <td className="px-6 py-4">
                          <div className="font-semibold text-white group-hover:text-teal-400 transition-colors uppercase tracking-tight">{post.title}</div>
                          <div className="text-xs text-slate-400 tracking-wider">/{post.slug}</div>
                        </td>
                        <td className="px-6 py-4">
                           <div className="flex justify-center">
                              <span className={`px-3 py-1 text-[10px] font-bold uppercase rounded-full border ${
                                post.type === 'blog' 
                                  ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' 
                                  : 'bg-purple-500/10 text-purple-400 border-purple-500/20'
                              }`}>
                                {post.type.replace('_', ' ')}
                              </span>
                           </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-400">{post.category || 'Uncategorized'}</td>
                        <td className="px-6 py-4 text-sm text-slate-400">
                          {new Date(post.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-3">
                            <Link to={`/admin/editor?id=${post.id}`} className="p-2 bg-white/5 rounded-lg hover:bg-teal-500/20 text-slate-400 hover:text-teal-400 transition-all border border-transparent hover:border-teal-500/30">
                              <Edit3 className="w-4 h-4" />
                            </Link>
                            <Link to={`/blog/${post.slug}`} target="_blank" className="p-2 bg-white/5 rounded-lg hover:bg-blue-500/20 text-slate-400 hover:text-blue-400 transition-all border border-transparent hover:border-blue-500/30">
                              <ExternalLink className="w-4 h-4" />
                            </Link>
                            <button 
                              onClick={() => deletePost(post.id)}
                              className="p-2 bg-white/5 rounded-lg hover:bg-red-500/20 text-slate-400 hover:text-red-400 transition-all border border-transparent hover:border-red-500/30"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
